/**
 * Mega menu tabs.
 *
 * Every panel is rendered up front and switched in place — no request, no
 * spinner. At a dozen products per tab that is a few KB of markup, and the
 * panel has to feel instantaneous or the hover interaction falls apart.
 *
 * Written against the ARIA tabs pattern: arrow keys move between tabs, Home
 * and End jump to the ends, and only the active tab is in the tab order so a
 * keyboard user steps past the row rather than through every tab in it.
 */

(() => {
  const init = (mega) => {
    if (mega.dataset.ready) return;
    mega.dataset.ready = 'true';

    const tabs = [...mega.querySelectorAll('[data-mega-tab]')];
    const panels = [...mega.querySelectorAll('[data-mega-panel]')];
    if (tabs.length < 2) return;

    const select = (index, focus) => {
      tabs.forEach((tab, i) => {
        const on = i === index;
        tab.classList.toggle('is-active', on);
        tab.setAttribute('aria-selected', String(on));
        tab.tabIndex = on ? 0 : -1;
        if (on && focus) tab.focus();
      });

      panels.forEach((panel, i) => {
        const on = i === index;
        panel.classList.toggle('is-active', on);
        // `hidden` is safe here because nothing gives .mega-panel a display,
        // unlike the dealer cards where a class had to do the hiding.
        panel.hidden = !on;
      });
    };

    tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => select(i, false));
      tab.addEventListener('keydown', (event) => {
        const last = tabs.length - 1;
        let next = null;
        if (event.key === 'ArrowRight') next = i === last ? 0 : i + 1;
        if (event.key === 'ArrowLeft') next = i === 0 ? last : i - 1;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = last;
        if (next === null) return;
        event.preventDefault();
        select(next, true);
      });
    });
  };

  /**
   * The trigger. Two modes, set from the theme editor.
   *
   * Click is the default and the safer one: a touch screen has no hover, and a
   * hover panel opens whenever the pointer merely crosses the menu on its way
   * somewhere else.
   *
   * Hover mode is driven from here rather than from a CSS `:hover` rule,
   * because CSS closes the instant the pointer leaves the trigger. The gap
   * between the menu item and a full-width panel anchored to the bottom of the
   * header is dead space belonging to neither, so the panel would shut while
   * the pointer was still travelling towards it. A short close delay — which
   * any re-entry cancels — is the only reliable fix.
   */
  const CLOSE_DELAY = 260;

  const initTrigger = (trigger) => {
    if (trigger.dataset.ready) return;
    trigger.dataset.ready = 'true';

    const item = trigger.closest('.has-mega');
    if (!item) return;

    const onHover = trigger.dataset.openOn === 'hover';
    item.classList.add(onHover ? 'opens-on-hover' : 'opens-on-click');

    let timer = null;
    const cancel = () => {
      clearTimeout(timer);
      timer = null;
    };

    const setOpen = (open) => {
      cancel();
      item.classList.toggle('is-open', open);
      trigger.setAttribute('aria-expanded', String(open));
    };

    const closeSoon = () => {
      cancel();
      timer = setTimeout(() => setOpen(false), CLOSE_DELAY);
    };

    // Click works in both modes — in hover mode it pins the panel open, which
    // is what a touch user gets since they generate no hover at all.
    trigger.addEventListener('click', (event) => {
      event.preventDefault();
      setOpen(!item.classList.contains('is-open'));
    });

    if (onHover) {
      // Bound to the item, not the trigger: the panel is a descendant, so the
      // pointer entering the panel counts as staying inside.
      item.addEventListener('pointerenter', () => setOpen(true));
      item.addEventListener('pointerleave', closeSoon);
    } else {
      // Click mode still closes when the pointer leaves entirely, but only
      // after the same grace period, so a clipped corner does not shut it.
      item.addEventListener('pointerleave', closeSoon);
      item.addEventListener('pointerenter', cancel);
    }

    // Escape closes and returns focus to the trigger, or focus is left inside
    // a panel that is no longer visible.
    item.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      setOpen(false);
      trigger.focus();
    });

    document.addEventListener('click', (event) => {
      if (!item.contains(event.target)) setOpen(false);
    });
  };

  const scan = () => {
    document.querySelectorAll('[data-mega]').forEach(init);
    document.querySelectorAll('[data-mega-trigger]').forEach(initTrigger);
  };

  scan();
  // The theme editor re-renders the header section on every setting change,
  // which replaces this markup with unbound copies.
  document.addEventListener('shopify:section:load', scan);
})();
